import React, { useCallback, useEffect, useState, useRef } from "react";
import { IEventMessage, IEventSummary, IShow, IBreed } from "@eagerdog/interfaces";
import { Constants, getTrialStatusLabel } from "@eagerdog/constants";
import { AxiosError } from "axios";

import moment from 'moment-timezone';

import { apiService } from "src/services/api.service";
import { getElementConfig, getShowTypeElements, isUkcRallyShow, UKC_RALLY_LEVELS } from "@eagerdog/constants";
import { webSocketService } from "src/services/websocket.service";
import { localStorageService } from "src/services/local-storage.service";
import { helperService } from "src/services/helper.service";
import { dogAgilityHeights } from "src/services/helper.service";
import { Config } from "@eagerdog/config";

import { toast } from "src/components/Toast/ToastManager";
import { download } from "src/components/Download/DownloadManager";
import { Modal, useModal } from "src/components/Modal/Modal";
import ContextMenu, { IContextMenuOption } from "src/components/ContextMenu/ContextMenu";
import Dropdown, { IOption } from "src/components/Dropdown/Dropdown";
import Button from "src/components/Button/Button";
import Input from "src/components/Input/Input";
import Checkbox from "src/components/Checkbox/Checkbox";
import JudgeBooksFilterModal from "../../../components/JudgeBooksFilterModal/JudgeBooksFilterModal";
import NoseworkJudgeBooksFilterModal from "../../../components/NoseworkJudgeBooksFilterModal/NoseworkJudgeBooksFilterModal";
import PrecisionCoursingJudgeBooksFilterModal from "../../../components/PrecisionCoursingJudgeBooksFilterModal/PrecisionCoursingJudgeBooksFilterModal";

import { type ColDef, GridApi, themeQuartz } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";

import { type IResultColumnsType, type IResultRows, getColumns } from "src/utils/ScoresGrid/getColumns";
import { getDataRows } from "src/utils/ScoresGrid/getDataRows";

import SendMessage, { SendMessageType } from "../../../components/SendMessage/SendMessage";
import EnterResults from "../../../components/EnterResults/EnterResults";
import ShowAwardsModal from "../../../components/ShowAwardsModal/ShowAwardsModal";
import NotificationBar, { INotification } from "../../../components/NotificationBar/NotificationBar";
import EnterRunOffWinnerModal from "../../../components/EnterRunOffWinnerModal/EnterRunOffWinnerModal";
import UKCConformationResults from "./UKCConformationResults";

import styles from "./ResultsTab.module.scss";

interface IProps {
  event: IEventSummary,
  currentTab: number,
  tabIndex: number
}

const ResultsTab: React.FC<IProps> = (props) => {
  const [loaded, setLoaded] = useState<boolean>(false);
  const [modalContent, setModalContent] = useState<"checkin" | "scoresheets" | "ribbons" | "ribbons-small" | "ribbons-5162" | "scoresheet-labels" | "results" | "judge_books" | "judgeBooksModal" | "noseworkJudgeBooksModal" | "precisionCoursingJudgeBooksModal" | "email" | "showAwards" | undefined>();
  const [gridApi, setGridApi] = useState<GridApi>();
  
  // Removed scroll position tracking - using AG-Grid's immutableData mode instead

  const [show, setShow] = useState<IOption>({ value: "None", id: "None" });
  const [showElement, _setShowElement] = useState<IOption>({ value: "None", id: "None" });
  const setShowElement = useCallback((value: IOption) => {
    _setShowElement(value);
  }, []);
  const [showLevel, setShowLevel] = useState<IOption>({ value: "None", id: "None" });
  const [showSection, setShowSection] = useState<IOption>({ value: "None", id: "None" });
  const [agilityJumpHeight, setAgilityJumpHeight] = useState<IOption>({ value: "None", id: "None" });

  const [rowData, setRowData] = useState<IResultRows>([]);
  const [scores, setScores] = useState<any[]>([]);

  // Enter Results modal state
  const [selectedResult, setSelectedResult] = useState<any>(null);
  const [showEnterResults, setShowEnterResults] = useState<boolean>(false);

  const [showOptions, setShowOptions] = useState<IOption[]>([]);
  const [shows, setShows] = useState<IShow[]>([]);
  const [updatingShowRunOrder, setUpdatingShowRunOrder] = useState<boolean>(false);

  // Change Status modal state (Trial Status feature)
  const [showChangeStatusModal, setShowChangeStatusModal] = useState<boolean>(false);
  const [statusFormValue, setStatusFormValue] = useState<IOption>({ id: "", value: "" });
  const [statusNoteValue, setStatusNoteValue] = useState<string>("");
  const [savingStatus, setSavingStatus] = useState<boolean>(false);

  const trialStatusOptions: IOption[] = [
    { id: Constants.trial_status.not_started, value: "Not Started" },
    { id: Constants.trial_status.running, value: "Running" },
    { id: Constants.trial_status.break, value: "Break" },
    { id: Constants.trial_status.complete, value: "Complete" },
  ];
  const showsRef = useRef<IShow[]>([]);
  
  // Keep showsRef in sync with shows
  useEffect(() => {
    showsRef.current = shows;
  }, [shows]);
  const [elementOptions, setElementOptions] = useState<IOption[]>([]);
  const [levelOptions, setLevelOptions] = useState<IOption[]>([]);
  const sectionOptions:IOption[] = [
    { value: "None", id: "None" },
    { value: "A", id: "A" },
    { value: "B", id: "B" },
    { value: "TO", id: "TO" },
    { value: "JR", id: "JR" }
  ];
  const abSectionOptions: IOption[] = [
    { value: "None", id: "None" },
    { value: "A", id: "A" },
    { value: "B", id: "B" },
  ];
  // 4-H Agility uses Division A/B sections (see Constants.dog_class_section.fh_agility_division_a/b)
  const fhAgilitySectionOptions: IOption[] = [
    { value: "None", id: "None" },
    { value: Constants.dog_class_section.fh_agility_division_a, id: Constants.dog_class_section.fh_agility_division_a },
    { value: Constants.dog_class_section.fh_agility_division_b, id: Constants.dog_class_section.fh_agility_division_b },
  ];
  const sdgSectionOptions: IOption[] = [
    { value: "None", id: "None" },
    { value: Constants.dog_class_section.sdg_level_1, id: Constants.dog_class_section.sdg_level_1 },
    { value: Constants.dog_class_section.sdg_level_2, id: Constants.dog_class_section.sdg_level_2 },
  ];
  // UKC Rally: Level RO1/RO2/RO3/ROM + Section A/B/C (RO1 only offers C)
  const ukcRallySectionOptions: IOption[] = [
    { value: "None", id: "None" },
    { value: Constants.dog_class_section.ukc_rally_a, id: Constants.dog_class_section.ukc_rally_a },
    { value: Constants.dog_class_section.ukc_rally_b, id: Constants.dog_class_section.ukc_rally_b },
    { value: Constants.dog_class_section.ukc_rally_c, id: Constants.dog_class_section.ukc_rally_c },
  ];
  const isUkcRally = (showData?: IShow) => isUkcRallyShow(props.event.sanctioning_club, showData?.show_type);

  // Level dropdown options for show types with a flat level list (no element layer)
  const getFlatLevelOptions = (showData: IShow, elementsObj: any): IOption[] => {
    const options: IOption[] = [{ id: "None", value: "None" }];
    if (isUkcRally(showData)) {
      // Sections live in their own dropdown; URX/RMX stay as multi-entry levels
      for (const level of [...UKC_RALLY_LEVELS, Constants.dog_class_element_level.urx, Constants.dog_class_element_level.rmx]) {
        options.push({ value: level, id: level });
      }
      return options;
    }
    for (const key in elementsObj) {
      const value = elementsObj[key];
      if (Array.isArray(value) && value.length === 0) {
        options.push({ value: key, id: key });
      }
    }
    return options;
  };

  // Show types/sanctioning clubs that support filtering Entries/Results by Section
  const showUsesSectionFilter = (showData?: IShow) =>
    !!showData && (
      isUkcRally(showData) ||
      (showData.show_type === Constants.show_type.nosework && props.event.sanctioning_club === Constants.sanctioning_club.UKC) ||
      (showData.show_type === Constants.show_type.obedience && props.event.sanctioning_club === Constants.sanctioning_club.C_WAGS) ||
      (showData.show_type === Constants.show_type.rally_obedience && props.event.sanctioning_club === Constants.sanctioning_club.C_WAGS) ||
      (showData.show_type === Constants.show_type.urban_rat_race && props.event.sanctioning_club === Constants.sanctioning_club.ABI) ||
      (showData.show_type === Constants.show_type.agility && props.event.sanctioning_club === Constants.sanctioning_club.FH) ||
      (showData.show_type === Constants.show_type.scent_dog_games && props.event.sanctioning_club === Constants.sanctioning_club.ABI) ||
      showData.show_type === Constants.show_type.barn_hunt
    );

  const getSectionOptionsForShow = (showData?: IShow): IOption[] => {
    if (isUkcRally(showData)) {
      return ukcRallySectionOptions;
    }
    if (showData?.show_type === Constants.show_type.agility && props.event.sanctioning_club === Constants.sanctioning_club.FH) {
      return fhAgilitySectionOptions;
    }
    if (showData?.show_type === Constants.show_type.scent_dog_games && props.event.sanctioning_club === Constants.sanctioning_club.ABI) {
      return sdgSectionOptions;
    }
    if (showData?.show_type === Constants.show_type.barn_hunt || showData?.show_type === Constants.show_type.nosework) {
      return abSectionOptions;
    }
    return sectionOptions;
  };
  const agilityJumpHeightOptions: IOption[] = [
    { value: "None", id: "None" },
    ...dogAgilityHeights,
  ];

  const [labelsToSkip, setLabelsToSkip] = useState<string>("0");

  const [isBulkEmailing, setIsBulkEmailing] = useState<boolean>(false);

  // Judge Books Filter Modal state
  const [breeds, setBreeds] = useState<IBreed[]>([]);

  // Run-off notification state
  const [notifications, setNotifications] = useState<INotification[]>([]);
  const [runOffs, setRunOffs] = useState<any[]>([]);
  const [selectedRunOff, setSelectedRunOff] = useState<any>(null);
  const [showRunOffModal, setShowRunOffModal] = useState<boolean>(false);

  const { isShown, toggle } = useModal();

  const [menuActions, setMenuActions] = useState<IContextMenuOption[]>([]);
  const latestResultsRequestIdRef = useRef(0);

  // Use refs to always get current filter values
  const showElementRef = useRef(showElement);
  const showLevelRef = useRef(showLevel);
  const showSectionRef = useRef(showSection);
  const agilityJumpHeightRef = useRef(agilityJumpHeight);
  const isComponentMountedRef = useRef<boolean>(true);
  
  useEffect(() => {
    showElementRef.current = showElement;
  }, [showElement]);
  
  useEffect(() => {
    showLevelRef.current = showLevel;
  }, [showLevel]);
  
  useEffect(() => {
    showSectionRef.current = showSection;
  }, [showSection]);

  useEffect(() => {
    agilityJumpHeightRef.current = agilityJumpHeight;
  }, [agilityJumpHeight]);

  const downloadForm = useCallback((form: string | undefined) => {
    if (form) {
      let standardDownload:string[] = ["checkin", "scoresheets", "scoresheets_condensed", "results", "judge_books", "judge_books_match", "ribbons", "ribbons-small", "ribbons-5162", "scoresheet-labels", "fastcat_scoresheet"];

      if (standardDownload.includes(form)) {
        let _downloadOptions: any = {
          type: form,
          eventId: props.event._id,
          showId: show.id
        };

        // Use refs to get current values instead of closure values
        const currentShowElement = showElementRef.current;
        const currentShowLevel = showLevelRef.current;
        const currentShowSection = showSectionRef.current;
        const currentAgilityJumpHeight = agilityJumpHeightRef.current;

        // CAT trials use the dedicated CAT scoresheet download endpoint.
        if (show.data?.show_type === Constants.show_type.cat && form === "scoresheets") {
          _downloadOptions.type = "cat_scoresheet";
        }

        //TODO Pass the actual show data so we can check the type
        if (show.value.includes("Fast CAT") && form === "scoresheets") {
          _downloadOptions.type = "akc_scoresheets"
        }

        if (show.value.includes("Fast CAT") && form === "scoresheets_condensed") {
          _downloadOptions.type = "akc_scoresheets_condensed"
        }

        if (currentShowElement.id !== "None") {
          _downloadOptions.showElement = currentShowElement.id;
        }

        if (currentShowLevel.id !== "None") {
          _downloadOptions.showLevel = currentShowLevel.id;
        }

        if (currentShowSection.id !== "None") {
          _downloadOptions.showSection = currentShowSection.id;
        }

        if (currentAgilityJumpHeight.id !== "None") {
          _downloadOptions.agilityJumpHeight = currentAgilityJumpHeight.id;
        }

        if (form === "ribbons") {
          _downloadOptions.labelsToSkip = Number(labelsToSkip);
        }

        if (form === "ribbons-small") {
          _downloadOptions.type = "ribbons"; // Use the same type as regular ribbons
          _downloadOptions.labelsToSkip = Number(labelsToSkip);
          _downloadOptions.small = true; // Add the small flag
        }

        if (form === "ribbons-5162") {
          _downloadOptions.type = "ribbons"; // Use the same type as regular ribbons
          _downloadOptions.labelsToSkip = Number(labelsToSkip);
          _downloadOptions.format_5162 = true; // Add the 5162 flag
        }

        if (form === "scoresheet-labels") {
          _downloadOptions.type = "scoresheet-labels"; // Use the dedicated scoresheet labels type
          _downloadOptions.labelsToSkip = Number(labelsToSkip);
        }

        if (show.id !== "None") {
          download.pushDownload(_downloadOptions);
        } else {
          toast.show({
            title: "Download Results",
            content: "Please select a specific show from the dropdown",
            duration: 10000,
            type: "fail"
          });
        }
      }
    }
  }, [props.event._id, show, labelsToSkip]);

  const buildMenuActions = useCallback((): IContextMenuOption[] => {
    // When no show is selected, only bulk email is available (it works event-wide)
    if (show.id === "None" || !show.data) {
      const noShowActions: IContextMenuOption[] = [];

      // BHA Barn Hunt supports an event-wide (all trials) results export
      if (props.event.sanctioning_club === Constants.sanctioning_club.BHA) {
        noShowActions.push({
          label: "Download Results (All Trials)",
          onClick: () => {
            download.pushDownload({
              type: "barn_hunt_results",
              eventId: props.event._id
            });
          }
        });
      }

      if (shows.some((item) => item.show_type === Constants.show_type.fast_cat)) {
        noShowActions.push({
          label: "Download Results (detailed)",
          onClick: () => {
            download.pushDownload({
              type: "fast_cat_results_detailed",
              eventId: props.event._id
            });
          }
        });
      }

      noShowActions.push({
        label: "Bulk Email Results",
        onClick: () => {
          setModalContent("email");
          toggle();
        }
      });

      return noShowActions;
    }

    const baseActions = {
      downloadCheckin: {
        label: "Download Check-in List",
        onClick: () => downloadForm("checkin")
      },
      downloadRingSheetsFormatted: {
        label: "Download Ring Sheets (formatted)",
        onClick: () => {
          if (show.id !== "None") {
            download.pushDownload({
              type: "ring-sheets",
              eventId: props.event._id,
              showId: show.id,
              builder_suffix: "formatted",
            });
          }
        }
      },
      downloadScoresheets: {
        label: "Download Scoresheets",
        onClick: () => downloadForm("scoresheets")
      },
      downloadScoresheetsCondensed: {
        label: "Download Scoresheets (condensed)",
        onClick: () => downloadForm("scoresheets_condensed")
      },
      downloadScoresheetsFormatted: {
        label: "Download Scoresheets (Formatted)",
        onClick: () => downloadForm("fastcat_scoresheet")
      },
      downloadResults: {
        label: "Download Results",
        onClick: () => downloadForm("results")
      },
      downloadResultsDetailed: {
        label: "Download Results (detailed)",
        onClick: () => {
          download.pushDownload({
            type: "fast_cat_results_detailed",
            eventId: props.event._id
          });
        }
      },
      bulkEmail: {
        label: "Bulk Email Results",
        onClick: () => {
          setModalContent("email");
          toggle();
        }
      },
      ribbonLabels: {
        label: "Download Ribbon Labels",
        onClick: () => {
          setModalContent("ribbons");
          toggle();
        }
      },
      ribbonLabelsSmall: {
        label: "Download Ribbon Labels (5160)",
        onClick: () => {
          setModalContent("ribbons-small");
          toggle();
        }
      },
      ribbonLabels5162: {
        label: "Download Ribbon Labels (5162)",
        onClick: () => {
          setModalContent("ribbons-5162");
          toggle();
        }
      },
      scoresheetLabels: {
        label: "Download Scoresheet Labels",
        onClick: () => {
          setModalContent("scoresheet-labels");
          toggle();
        }
      },
      judgesBooksMatch: {
        label: "Download Judges Books (Match)",
        onClick: () => downloadForm("judge_books_match")
      },
      downloadJudgeBreeds: {
        label: "Download Judge Breeds",
        onClick: () => {
          if (show.id !== "None") {
            download.pushDownload({ type: "judge_breeds", eventId: props.event._id, showId: show.id });
          }
        }
      },
      downloadCounts: {
        label: "Download Counts",
        onClick: () => {
          if (show.id !== "None") {
            download.pushDownload({ type: "counts", eventId: props.event._id, showId: show.id });
          }
        }
      },
      downloadCountsFormatted: {
        label: "Download Counts (Formatted)",
        onClick: () => {
          if (show.id !== "None") {
            download.pushDownload({ type: "counts_formatted", eventId: props.event._id, showId: show.id });
          }
        }
      },
      downloadTrialReport: {
        label: "Download Trial Report",
        onClick: () => {
          if (show.id !== "None") {
            download.pushDownload({ type: "trial", eventId: props.event._id, showId: show.id });
          }
        }
      },
      downloadGroupJudgesBook: {
        label: "Download Group Judges Book",
        onClick: () => {
          if (show.id !== "None") {
            download.pushDownload({ type: "judge_books_group", eventId: props.event._id, showId: show.id });
          }
        }
      },
      downloadBisJudgesBook: {
        label: "Download BIS Judge Book",
        onClick: () => {
          if (show.id !== "None") {
            download.pushDownload({ type: "judge_books_show", eventId: props.event._id, showId: show.id });
          }
        }
      }
    };

    const createJudgesBooksAction = (): IContextMenuOption => ({
      label: "Download Judges Books",
      onClick: () => {
        if (props.event.sanctioning_club === Constants.sanctioning_club.UKC &&
          show.data?.show_type === Constants.show_type.nosework) {
          setModalContent("noseworkJudgeBooksModal");
          toggle();
          return;
        }

        if (props.event.sanctioning_club === Constants.sanctioning_club.UKC &&
          show.data?.show_type === Constants.show_type.precision_coursing) {
          setModalContent("precisionCoursingJudgeBooksModal");
          toggle();
          return;
        }

        if (props.event.sanctioning_club === Constants.sanctioning_club.UKC &&
          show.data?.show_type === Constants.show_type.conformation) {
          setModalContent("judgeBooksModal");
          toggle();
        } else {
          downloadForm("judge_books");
        }
      }
    });

    if (show.data?.show_type === Constants.show_type.fast_cat) {
      return [
        baseActions.downloadScoresheetsFormatted,
        baseActions.downloadScoresheets,
        baseActions.downloadScoresheetsCondensed,
        baseActions.ribbonLabelsSmall,
        baseActions.downloadResults,
        baseActions.downloadResultsDetailed,
        baseActions.bulkEmail
      ];
    }

    if (show.data?.show_type === Constants.show_type.cat) {
      return [
        baseActions.downloadScoresheets,
        baseActions.ribbonLabelsSmall,
        baseActions.bulkEmail
      ];
    }

    if (props.event.sanctioning_club === Constants.sanctioning_club.AKC &&
      [Constants.show_type.rally_obedience, Constants.show_type.obedience].includes(show.data?.show_type)) {
      return [
        baseActions.downloadCheckin,
        baseActions.downloadRingSheetsFormatted,
        baseActions.downloadScoresheets,
        baseActions.ribbonLabelsSmall,
        baseActions.downloadResults,
        createJudgesBooksAction(),
        baseActions.bulkEmail
      ];
    }

    // AKC Scentwork - ribbon labels on Avery 5160 (small) or Avery 5162
    if (props.event.sanctioning_club === Constants.sanctioning_club.AKC &&
      show.data?.show_type === Constants.show_type.scentwork) {
      return [
        baseActions.downloadCheckin,
        baseActions.downloadScoresheets,
        baseActions.ribbonLabelsSmall,
        baseActions.ribbonLabels5162,
        baseActions.downloadResults,
        createJudgesBooksAction(),
        baseActions.bulkEmail
      ];
    }

    if (show.data?.show_type === Constants.show_type.nosework) {
      return [
        baseActions.downloadCheckin,
        baseActions.downloadScoresheets,
        baseActions.ribbonLabels,
        baseActions.ribbonLabelsSmall,
        baseActions.ribbonLabels5162,
        baseActions.downloadResults,
        createJudgesBooksAction(),
        baseActions.judgesBooksMatch,
        baseActions.bulkEmail
      ];
    }

    // UKC Agility gets the scoresheet labels option
    if (props.event.sanctioning_club === Constants.sanctioning_club.UKC && 
        show.data?.show_type === Constants.show_type.agility) {
      return [
        baseActions.downloadCheckin,
        baseActions.downloadScoresheets,
        baseActions.ribbonLabels,
        baseActions.ribbonLabelsSmall,
        baseActions.scoresheetLabels,
        baseActions.downloadResults,
        createJudgesBooksAction(),
        baseActions.bulkEmail
      ];
    }

    // UKC Obedience and Rally Obedience
    if (props.event.sanctioning_club === Constants.sanctioning_club.UKC &&
        [Constants.show_type.rally_obedience, Constants.show_type.obedience].includes(show.data?.show_type)) {
      return [
        baseActions.downloadCheckin,
        baseActions.downloadScoresheets,
        baseActions.ribbonLabels,
        baseActions.ribbonLabelsSmall,
        baseActions.ribbonLabels5162,
        baseActions.downloadResults,
        createJudgesBooksAction(),
        baseActions.bulkEmail
      ];
    }

    // ABI Urban Rat Race
    if (props.event.sanctioning_club === Constants.sanctioning_club.ABI &&
        show.data?.show_type === Constants.show_type.urban_rat_race) {
      return [
        baseActions.downloadCheckin,
        baseActions.downloadScoresheets,
        baseActions.ribbonLabels5162,
        baseActions.downloadResults,
        createJudgesBooksAction(),
        baseActions.bulkEmail
      ];
    }

    // ABI Scent Dog Games
    if (props.event.sanctioning_club === Constants.sanctioning_club.ABI &&
        show.data?.show_type === Constants.show_type.scent_dog_games) {
      return [
        baseActions.ribbonLabelsSmall,
        baseActions.ribbonLabels5162,
        baseActions.downloadResults,
        createJudgesBooksAction(),
        baseActions.bulkEmail
      ];
    }

    // ABI Lost and Found
    if (props.event.sanctioning_club === Constants.sanctioning_club.ABI &&
        show.data?.show_type === Constants.show_type.lost_and_found) {
      return [
        baseActions.ribbonLabelsSmall,
        baseActions.ribbonLabels5162,
        baseActions.downloadResults,
        createJudgesBooksAction(),
        baseActions.bulkEmail,
      ];
    }

    // AKC ATT - just scoresheets and results (no ribbons, no check-in)
    if (props.event.sanctioning_club === Constants.sanctioning_club.AKC &&
        show.data?.show_type === Constants.show_type.att) {
      return [
        baseActions.downloadScoresheets,
        baseActions.downloadResults,
        baseActions.bulkEmail
      ];
    }

    // UKC Conformation
    if (props.event.sanctioning_club === Constants.sanctioning_club.UKC &&
        show.data?.show_type === Constants.show_type.conformation) {
      return [
        baseActions.downloadJudgeBreeds,
        createJudgesBooksAction(),
        baseActions.downloadGroupJudgesBook,
        baseActions.downloadBisJudgesBook,
        baseActions.downloadCounts,
        baseActions.downloadCountsFormatted,
        baseActions.downloadTrialReport,
        baseActions.bulkEmail
      ];
    }

    // BHA Barn Hunt - scoresheets, small (Avery 5160) ribbon labels, and per-trial results
    if (props.event.sanctioning_club === Constants.sanctioning_club.BHA &&
        show.data?.show_type === Constants.show_type.barn_hunt) {
      return [
        baseActions.downloadScoresheets,
        baseActions.ribbonLabelsSmall,
        baseActions.downloadResults,
      ];
    }

    // General Obedience Show N Go / General Rally - bulk email only
    if (props.event.sanctioning_club === Constants.sanctioning_club.GENERAL &&
       (show.data?.show_type === Constants.show_type.obedience_show_n_go ||
        show.data?.show_type === Constants.show_type.general_rally)) {
      return [
        baseActions.bulkEmail
      ];
    }

    // UKC Dock Jumping - results, judges books, bulk email only
    if (show.data?.show_type === Constants.show_type.dock_jumping) {
      return [
        baseActions.downloadResults,
        createJudgesBooksAction(),
        baseActions.bulkEmail
      ];
    }

    // AKC Fetch has no judge books and no Avery 5162 labels
    if (props.event.sanctioning_club === Constants.sanctioning_club.AKC &&
        show.data?.show_type === Constants.show_type.fetch) {
      return [
        baseActions.downloadCheckin,
        baseActions.downloadScoresheets,
        baseActions.ribbonLabels,
        baseActions.ribbonLabelsSmall,
        baseActions.downloadResults,
        baseActions.bulkEmail
      ];
    }

    return [
      baseActions.downloadCheckin,
      baseActions.downloadScoresheets,
      baseActions.ribbonLabels,
      baseActions.ribbonLabelsSmall,
      baseActions.ribbonLabels5162,
      baseActions.downloadResults,
      createJudgesBooksAction(),
      baseActions.bulkEmail
    ];
  }, [downloadForm, props.event, show, shows, scores, toggle]);

  useEffect(() => {
    const actions = buildMenuActions();
    setMenuActions(actions);
  }, [buildMenuActions, downloadForm]);

  const theme = themeQuartz.withParams({
    fontFamily: "Roboto",
    headerFontFamily: "Roboto",
    cellFontFamily: "Roboto"
  });

  const bulkEmailResults = (payload: IEventMessage) => {
    if (!isBulkEmailing) {
      setIsBulkEmailing(true);
      const currentUser = helperService.getUser();
      const testRecipientText = currentUser?.email
        ? `Test email sent to ${currentUser.email}.`
        : "Test email sent.";

      if (show.id === "None") {
        const content = payload.is_test_sending
          ? testRecipientText
          : "You've successfully bulk emailed all event results"

        apiService.requestResultsEmailForEvent(props.event._id, payload).then((response) => {
          toast.show({
            title: "Bulk Email Results",
            content,
            duration: 10000,
            type: "success"
          });  
        }).catch((e: AxiosError) => {
          toast.show({
            title: "Bulk Email Results",
            // @ts-expect-error
            content: `Unable to bulk email results. ${e.response?.data?.message}`,
            duration: 10000,
            errorDetails: e,
            type: "fail"
          });
        }).finally(() => {
          toggle();
          setIsBulkEmailing(false);
        });
      } else {
        const content = payload.is_test_sending
          ? testRecipientText
          : "You've successfully bulk emailed all show results"
        apiService.requestResultsEmailForShow(props.event._id, show.id, payload).then((response) => {
          toast.show({
            title: "Bulk Email Results",
            content,
            duration: 10000,
            type: "success"
          });  
        }).catch((e: AxiosError) => {
          toast.show({
            title: "Bulk Email Results",
            // @ts-expect-error
            content: `Unable to bulk email results. ${e.response?.data?.message}`,
            duration: 10000,
            errorDetails: e,
            type: "fail"
          });
        }).finally(() => {
          toggle();
          setIsBulkEmailing(false);
        });
      }
    }
  }

  const getModalContent = () => {
    if (modalContent === "email") {
      const eventDate = new Intl.DateTimeFormat("en-US", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        timeZone: props.event.timezone || "America/Toronto",
      }).format(new Date(props.event.start_date))

      return <SendMessage
        isSending={isBulkEmailing}
        handleSendMessage={bulkEmailResults}
        subject={`${props.event.title} Results - ${eventDate}`}
        messageBody={`
          <p>Congratulations on completing another great event!</p>
          <p>Please find your results attached for all dogs you competed with.</p>
          <p>${props.event.club.club_name} - ${props.event.club.email} - ${props.event.club.phone}</p>
        `}
        type={SendMessageType.BulkEmailResults}
        eventId={props.event._id}
        topText={show.id === "None" ?
          <p>You are about to bulk email <i>all</i> registrants to this event. <i>It is recommended you only do this once</i>. You should completely enter <i>all results to all shows</i> before doing this, have you completed entering all results?</p>
          :
          <p>You are about to bulk email <i>all</i> registrants to {show.value}. <i>It is recommended you only do this once</i>. You should completely enter <i>all results to {show.value}</i> before doing this, have you completed entering all results for {show.value}?</p>
        }
      />
    } else {
      let title:string = "";

      switch (modalContent) {
        case "checkin":
          title = "Download Check-in List";
        break;
        case "scoresheets":
          title = "Download Scoresheets";
        break;
        case "ribbons":
          title = "Download Ribbon Labels";
        break;
        case "ribbons-small":
          title = "Download Ribbon Labels (5160)";
        break;
        case "ribbons-5162":
          title = "Download Ribbon Labels (5162)";
        break;
        case "scoresheet-labels":
          title = "Download Scoresheet Labels";
          break;
        case "results":
          title = "Download Results";
        break;
        case "judge_books":
          title = "Download Judges Book";
        break;
        case "judgeBooksModal":
          return <JudgeBooksFilterModal
            onDownload={(filterData) => {
              const downloadParams: any = {
                type: "judge_books",
                eventId: props.event._id,
                showId: show.id,
                breed_filter_type: filterData.breed_filter_type
              };

              if (showElement.id !== "None") {
                downloadParams.showElement = showElement.id;
              }

              if (showLevel.id !== "None") {
                downloadParams.showLevel = showLevel.id;
              }

              if (showSection.id !== "None") {
                downloadParams.showSection = showSection.id;
              }

              if (filterData.breed_filters && filterData.breed_filters.length > 0) {
                downloadParams.breed_filters = filterData.breed_filters;
              }

              if (filterData.breed_group_filters && filterData.breed_group_filters.length > 0) {
                downloadParams.breed_group_filters = filterData.breed_group_filters;
              }

              if (show.id !== "None") {
                download.pushDownload(downloadParams);
                toggle();
              } else {
                toast.show({
                  title: "Download Judge's Books",
                  content: "Please select a specific show from the dropdown",
                  duration: 10000,
                  type: "fail"
                });
              }
            }}
            onCancel={() => { toggle(); }}
            preloadedBreeds={breeds}
            eventId={props.event._id}
            showId={show.id}
            showElement={showElement.id !== "None" ? showElement.id : undefined}
            showLevel={showLevel.id !== "None" ? showLevel.id : undefined}
            showSection={showSection.id !== "None" ? showSection.id : undefined}
          />;
        case "noseworkJudgeBooksModal": {
          const showScores = scores.filter(s => s.show_id === show.id);
          const hasIncomplete = showScores.some(s => s.status !== Constants.score_status.complete);
          return <NoseworkJudgeBooksFilterModal
            hasIncompleteResults={hasIncomplete}
            onDownload={(sortOrder) => {
              if (show.id !== "None") {
                const downloadParams: any = {
                  type: "judge_books",
                  eventId: props.event._id,
                  showId: show.id,
                  judge_books_sort: sortOrder,
                };
                if (showElement.id !== "None") downloadParams.showElement = showElement.id;
                if (showLevel.id !== "None") downloadParams.showLevel = showLevel.id;
                if (showSection.id !== "None") downloadParams.showSection = showSection.id;
                download.pushDownload(downloadParams);
                toggle();
              } else {
                toast.show({
                  title: "Download Judge's Books",
                  content: "Please select a specific show from the dropdown",
                  duration: 10000,
                  type: "fail"
                });
              }
            }}
            onCancel={() => { toggle(); }}
          />;
        }
        case "precisionCoursingJudgeBooksModal":
          return <PrecisionCoursingJudgeBooksFilterModal
            onDownload={(sortOrder) => {
              if (show.id !== "None") {
                download.pushDownload({
                  type: "judge_books",
                  eventId: props.event._id,
                  showId: show.id,
                  judge_books_sort: sortOrder,
                });
                toggle();
              } else {
                toast.show({
                  title: "Download Judge's Books",
                  content: "Please select a specific show from the dropdown",
                  duration: 10000,
                  type: "fail"
                });
              }
            }}
            onCancel={() => { toggle(); }}
          />;
        case "showAwards":
          if (show.id === "None" || !show.data) {
            toast.show({
              title: "Show Awards",
              content: "Please select a specific show from the dropdown",
              duration: 5000,
              type: "fail"
            });
            toggle();
            return <></>;
          }
          return <ShowAwardsModal
            event={props.event}
            show={show.data}
            onClose={() => { toggle(); }}
          />;
      }

      return <form className={styles.downloadModalInner} onSubmit={(e) => { e.preventDefault(); downloadForm(modalContent); toggle(); }}>
        <div className={styles.title}>{title}</div>
        <div className={styles.modalInner}>
          {modalContent === "ribbons" && <Input type="text" onChange={(e:any) => { setLabelsToSkip(e.target.value); }} label="Labels to Skip" placeholder="How many labels do you want to skip?" defaultValue={labelsToSkip} />  }
          {modalContent === "ribbons-small" && <Input type="text" onChange={(e:any) => { setLabelsToSkip(e.target.value); }} label="Labels to Skip" placeholder="How many labels do you want to skip?" defaultValue={labelsToSkip} />  }
          {modalContent === "ribbons-5162" && <Input type="text" onChange={(e:any) => { setLabelsToSkip(e.target.value); }} label="Labels to Skip" placeholder="How many labels do you want to skip?" defaultValue={labelsToSkip} />  }
          {modalContent === "scoresheet-labels" && <Input type="text" onChange={(e:any) => { setLabelsToSkip(e.target.value); }} label="Labels to Skip" placeholder="How many labels do you want to skip?" defaultValue={labelsToSkip} />  }
        </div>
        <div className={styles.modalActions}>
          <Button type="submit">Download</Button>
        </div>
      </form>;
    }
  }

  const getResults = useCallback((id: string, freshRunOffs?: any[]) => {
    const requestId = ++latestResultsRequestIdRef.current;
    const runOffsToUse = freshRunOffs !== undefined ? freshRunOffs : runOffs;
    let query:any = {
      sort: [{
        attribute_name: "show_type",
        sort: "asc"
      }, {
        attribute_name: "created_at",
        sort: "asc"
      }],
    };

    return apiService.getEventScores(id, query).then((response) => {
      // Ignore stale/out-of-order responses to prevent UI flicker
      // (e.g., checkboxes briefly reverting then flipping back).
      if (requestId !== latestResultsRequestIdRef.current) {
        return response;
      }
      setScores(response);
      setRowData(getDataRows(response, props.event, runOffsToUse));
      return response;
    });
  }, [props.event, runOffs]);

  // Store getResults in a ref so websocket callback can access it without causing re-renders
  const getResultsRef = useRef(getResults);
  useEffect(() => {
    getResultsRef.current = getResults;
  }, [getResults]);

  // WebSocket connection for real-time updates - runs once per event
  useEffect(() => {
    if (!isComponentMountedRef.current) {
      isComponentMountedRef.current = true;
    }

    let cleanupFunctions: (() => void)[] = [];

    const setupWebSocket = async () => {
      try {
        const token = localStorageService.getItem(localStorageService.ACCESS_TOKEN);
        if (!token) {
          return;
        }

        // Connect to websocket
        await webSocketService.connect({
          url: Config.web.REACT_APP_API_URL || 'http://localhost:3001',
          namespace: '/run-order',
          token,
        });

        // Subscribe to connection status to know when we're actually connected
        const unsubscribeStatus = webSocketService.onConnectionStatus((status) => {
          console.log('[ResultsTab][WS] connection_status', status);
          if (status.connected && isComponentMountedRef.current && showsRef.current?.length > 0) {
            // NOW join rooms for all shows (only when actually connected AND shows are loaded)
            showsRef.current.forEach(eventShow => {
              if (eventShow.rings && eventShow.rings.length > 0) {
                eventShow.rings.forEach(ring => {
                  const roomData = {
                    eventId: props.event._id,
                    showId: eventShow._id,
                    ring: ring.ring,
                  };
                  console.log('[ResultsTab][WS] join room', roomData);
                  webSocketService.joinRunOrderRoom(roomData);
                });
              } else {
                // For shows without rings (e.g., AKC Obedience), join a default room
                const roomData = {
                  eventId: props.event._id,
                  showId: eventShow._id,
                  ring: "1",
                };
                console.log('[ResultsTab][WS] join default room', roomData);
                webSocketService.joinRunOrderRoom(roomData);
              }
            });
          }
        });
        cleanupFunctions.push(unsubscribeStatus);

        // Subscribe to run order updates (which include check-in and result updates)
        const unsubscribeUpdates = webSocketService.onRunOrderUpdate(async (data) => {
          console.log('[ResultsTab][WS] run_order_update received', data);
          if (!isComponentMountedRef.current) {
            return;
          }

          // Check if update is for this event
          if (data.eventId === props.event._id && data.reload) {
            // Reload run-offs first if this is for an Obedience show (AKC or UKC)
            if (data.showId && (props.event.sanctioning_club === Constants.sanctioning_club.AKC || props.event.sanctioning_club === Constants.sanctioning_club.UKC)) {
              // Find the show to check its type
              const affectedShow = showsRef.current.find(s => s._id === data.showId);
              if (affectedShow && affectedShow.show_type === Constants.show_type.obedience) {
                const freshRunOffs = await loadRunOffs(props.event._id, data.showId);
                // Pass the fresh run-offs directly to getResults to avoid stale closure
                await getResultsRef.current(props.event._id, freshRunOffs);
                return;
              }
            }
            // Use ref to avoid dependency issues
            getResultsRef.current(props.event._id);
          }
        });
        cleanupFunctions.push(unsubscribeUpdates);

      } catch (error) {
        // WebSocket setup failed - log error for debugging
      }
    };

    // Start the async setup immediately
    setupWebSocket();

    return () => {
      // Cleanup on unmount or event change
      isComponentMountedRef.current = false;
      cleanupFunctions.forEach(fn => fn());
      webSocketService.leaveRunOrderRoom();
    };
  }, [props.event._id]); // Only re-run when event changes
  
  // Separate effect to join rooms when shows load (after websocket is already connected)
  useEffect(() => {
    if (shows.length > 0 && isComponentMountedRef.current) {
      // Join rooms for all shows
      shows.forEach(eventShow => {
        if (eventShow.rings && eventShow.rings.length > 0) {
          eventShow.rings.forEach(ring => {
            const roomData = {
              eventId: props.event._id,
              showId: eventShow._id,
              ring: ring.ring,
            };
            console.log('[ResultsTab][WS] join room on shows load', roomData);
            webSocketService.joinRunOrderRoom(roomData);
          });
        } else {
          // For shows without rings (e.g., AKC Obedience), join a default room
          const roomData = {
            eventId: props.event._id,
            showId: eventShow._id,
            ring: "1",
          };
          console.log('[ResultsTab][WS] join default room on shows load', roomData);
          webSocketService.joinRunOrderRoom(roomData);
        }
      });
    }
  }, [shows.length, props.event._id]);

  // Load run-offs for the selected show
  const loadRunOffs = useCallback(async (eventId: string, showId: string) => {
    if (!showId || showId === "None") {
      setRunOffs([]);
      setNotifications([]);
      return [];
    }

    try {
      const response = await apiService.getRunOffs(eventId, showId);
      const fetchedRunOffs = (response.run_offs || []).map((runOff: any) => ({
        ...runOff,
        show_id: showId,
      }));
      setRunOffs(fetchedRunOffs);

      // Convert run-offs to notifications
      const newNotifications: INotification[] = fetchedRunOffs
        .filter((runOff: any) => !runOff.resolved_at) // Only show unresolved run-offs
        .sort((a: any, b: any) => {
          // Sort alphabetically by level
          const levelA = `${a.level}`;
          const levelB = `${b.level}`;
          return levelA.localeCompare(levelB);
        })
        .map((runOff: any, index: number) => ({
          id: `runoff-${index}`,
          type: 'run-off' as const,
          message: `Multiple dogs are tied in ${runOff.level}. Once you complete the run off, enter the winning dog.`,
          ctaButton: {
            label: 'Enter Winner',
            onClick: () => {
              const runOffIndex = fetchedRunOffs.findIndex((ro: any) => 
                ro.show_element === runOff.show_element &&
                ro.level === runOff.level &&
                ro.placement === runOff.placement &&
                !ro.resolved_at
              );
              setSelectedRunOff({ ...runOff, index: runOffIndex, show_id: runOff.show_id || showId });
              setShowRunOffModal(true);
            }
          },
          dismissible: false,
          metadata: runOff
        }));

      setNotifications(newNotifications);
      return fetchedRunOffs;
    } catch (error) {
      setRunOffs([]);
      setNotifications([]);
      return [];
    }
  }, []);

  // Determine if Show Awards button should be visible
  const shouldShowAwardsButton = useCallback(() => {
    if (show.id === "None" || !show.data) {
      return false;
    }

    // AKC Obedience, Rally Obedience, or Scentwork
    if (props.event.sanctioning_club === Constants.sanctioning_club.AKC &&
        [Constants.show_type.obedience, Constants.show_type.rally_obedience, Constants.show_type.scentwork].includes(show.data.show_type)) {
      return true;
    }

    // UKC Obedience, Rally Obedience, or Agility
    if (props.event.sanctioning_club === Constants.sanctioning_club.UKC &&
        [Constants.show_type.obedience, Constants.show_type.rally_obedience, Constants.show_type.agility].includes(show.data.show_type)) {
      return true;
    }

    return false;
  }, [show, props.event.sanctioning_club]);

  const handleShowAwards = () => {
    if (show.id === "None" || !show.data) {
      toast.show({
        title: "Show Awards",
        content: "Please select a specific show from the dropdown",
        duration: 5000,
        type: "fail"
      });
      return;
    }
    setModalContent("showAwards");
    toggle();
  };

  // Recalculate row data when runOffs change
  useEffect(() => {
    if (scores.length > 0) {
      setRowData(getDataRows(scores, props.event, runOffs));
    }
  }, [runOffs, scores, props.event]);

  let colDefs: ColDef<IResultColumnsType>[] = getColumns(show, props.event, shows, getResults, runOffs, undefined, {
    onShowsChange: setShows,
    scores,
  });

  const defaultColDef: ColDef = {
    flex: 1
  };

  const onFirstDataRendered = (params:any) => {
    const queryParameters = new URLSearchParams(window.location.search);
    const paramShow = queryParameters.get("show");

    if (paramShow) {
      params.api.setFilterModel({
          "show": {
              "values": [ paramShow ],
              "filterType": "set"
          }
      });
    }
  }

  const runOrderSupportedShowTypes = useRef<string[]>([
    Constants.show_type.obedience,
    Constants.show_type.rally_obedience,
    Constants.show_type.scentwork,
    Constants.show_type.conformation,
    Constants.show_type.nosework,
    Constants.show_type.water_dog,
    Constants.show_type.country_rat_race,
    Constants.show_type.agility,
    Constants.show_type.fast_cat,
    Constants.show_type.fetch,
    Constants.show_type.barn_hunt,
    Constants.show_type.shed_dog,
    Constants.show_type.precision_coursing,
  ]);

  const selectedShowSupportsRunOrder =
    show.id !== "None" &&
    !!show.data &&
    runOrderSupportedShowTypes.current.includes((show.data as IShow).show_type) &&
    (show.data as IShow).show_type !== Constants.show_type.conformation;

  const isUKCConformationShow =
    show.id !== "None" &&
    !!show.data &&
    (show.data as IShow).show_type === Constants.show_type.conformation &&
    props.event.sanctioning_club === Constants.sanctioning_club.UKC;

  const buildShowOption = useCallback((eventShow: IShow): IOption => ({
    value: `${eventShow.show_type} ${eventShow.show_name} (${moment(eventShow.show_date).format("ddd, h:mm a")})`,
    id: eventShow._id,
    data: eventShow,
  }), []);

  const handleShowRunOrderToggle = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (show.id === "None" || !show.data) {
      return;
    }

    const selectedShow = show.data as IShow;
    const newValue = e.target.checked;

    try {
      setUpdatingShowRunOrder(true);
      const showUpdateData = {
        show_name: selectedShow.show_name,
        show_type: selectedShow.show_type,
        show_date: selectedShow.show_date,
        fee: selectedShow.fee,
        use_early_bird_fee: selectedShow.use_early_bird_fee,
        use_same_fee: selectedShow.use_same_fee,
        use_registration_end_date: selectedShow.use_registration_end_date,
        show_elements: selectedShow.show_elements,
        rings: selectedShow.rings,
        show_run_order: newValue,
        ...(selectedShow.club_name && { club_name: selectedShow.club_name }),
        ...(selectedShow.show_club_id && { show_club_id: selectedShow.show_club_id }),
        ...(selectedShow.event_number && { event_number: selectedShow.event_number }),
        ...(selectedShow.trailing_show !== undefined && { trailing_show: selectedShow.trailing_show }),
        ...(selectedShow.previous_show_name && { previous_show_name: selectedShow.previous_show_name }),
        ...(selectedShow.registration_end_date && { registration_end_date: selectedShow.registration_end_date }),
        ...(selectedShow.early_bird_end_date && { early_bird_end_date: selectedShow.early_bird_end_date }),
        ...(selectedShow.early_bird_fee !== undefined && { early_bird_fee: selectedShow.early_bird_fee }),
        ...(selectedShow.is_archived !== undefined && { is_archived: selectedShow.is_archived }),
        ...(selectedShow.max_dogs !== undefined && { max_dogs: selectedShow.max_dogs }),
        ...(selectedShow.first_run_time_minutes !== undefined && { first_run_time_minutes: selectedShow.first_run_time_minutes }),
        ...(selectedShow.run_duration_minutes !== undefined && { run_duration_minutes: selectedShow.run_duration_minutes }),
        ...(selectedShow.breed_judges && { breed_judges: selectedShow.breed_judges }),
        ...(selectedShow.breed_group_judges && { breed_group_judges: selectedShow.breed_group_judges }),
        ...(selectedShow.arm_number_start !== undefined && { arm_number_start: selectedShow.arm_number_start }),
        ...(selectedShow.specialty_breed && { specialty_breed: selectedShow.specialty_breed }),
        ...(selectedShow.waitlist_option && { waitlist_option: selectedShow.waitlist_option }),
        ...(selectedShow.waitlist_limit !== undefined && { waitlist_limit: selectedShow.waitlist_limit }),
        ...(selectedShow.waitlist_enabled !== undefined && { waitlist_enabled: selectedShow.waitlist_enabled }),
        ...(selectedShow.waitlist_size !== undefined && { waitlist_size: selectedShow.waitlist_size }),
      };

      const updatedShow = await apiService.updateShow(props.event._id, selectedShow._id, showUpdateData);
      const updatedOption = buildShowOption(updatedShow);

      setShows((prev) => prev.map((s) => (s._id === updatedShow._id ? updatedShow : s)));
      setShow(updatedOption);
      setShowOptions((prev) =>
        prev.map((option) => (option.id === updatedShow._id ? updatedOption : option))
      );
    } catch (error: any) {
      toast.show({
        title: "Run Order",
        content: "Failed to update run order visibility setting. Please try again.",
        type: "fail",
        duration: 7000,
      });
    } finally {
      setUpdatingShowRunOrder(false);
    }
  }, [show, props.event._id, buildShowOption]);

  // Populate the Change Status modal from the currently selected show whenever it opens
  useEffect(() => {
    if (!showChangeStatusModal || show.id === "None" || !show.data) {
      return;
    }
    const selectedShow = show.data as IShow;
    const currentStatus = selectedShow.show_status || Constants.trial_status.not_started;
    setStatusFormValue(
      trialStatusOptions.find((o) => o.id === currentStatus) || trialStatusOptions[0]
    );
    setStatusNoteValue(selectedShow.show_status_note || "");
  }, [showChangeStatusModal, show]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaveStatus = useCallback(async () => {
    if (show.id === "None" || !show.data || !statusFormValue.id) {
      return;
    }

    const selectedShow = show.data as IShow;

    try {
      setSavingStatus(true);
      const updatedShow = await apiService.updateShowStatus(props.event._id, selectedShow._id, {
        show_status: statusFormValue.id,
        show_status_note: statusNoteValue.trim() || undefined,
      });
      const updatedOption = buildShowOption(updatedShow);

      setShows((prev) => prev.map((s) => (s._id === updatedShow._id ? updatedShow : s)));
      setShow(updatedOption);
      setShowOptions((prev) =>
        prev.map((option) => (option.id === updatedShow._id ? updatedOption : option))
      );

      toast.show({
        title: "Trial Status",
        content: "Trial status updated.",
        type: "success",
        duration: 5000,
      });
      setShowChangeStatusModal(false);
    } catch (error: any) {
      toast.show({
        title: "Trial Status",
        content: "Failed to update trial status. Please try again.",
        type: "fail",
        duration: 7000,
      });
    } finally {
      setSavingStatus(false);
    }
  }, [show, props.event._id, buildShowOption, statusFormValue, statusNoteValue]);

  const getShows = (clubId: string, eventId: string) => {
    apiService.getClubEventShows(clubId, eventId).then((response) => {
      setShows(response);

      let options:IOption[] = [{ value: "None", id: "None" }];

      for (let i in response) {
        options.push(buildShowOption(response[i]));
      }

      setShowOptions(options);
    });
  }

  const isEmptyOptions = (options: IOption[]) => {
    if (options.length === 0) {
      return true;
    } else if (options.length === 1) {
      if (options[0].id === "None") {
        return true;
      } else {
        return false;
      }
    }

    return false;
  }

  const onGridReady = (params: any) => {
    setGridApi(params.api);
  }

  const resetFilters = useCallback(() => {
    if (gridApi) {
      setShow({ id: "None", value: "None" });
      setShowElement({ id: "None", value: "None" });
      setShowLevel({ id: "None", value: "None" });
      setShowSection({ id: "None", value: "None" });
      setAgilityJumpHeight({ id: "None", value: "None" });
      gridApi.setFilterModel(null);
    }
  }, [gridApi]);

  const handleRowClick = useCallback((event: any) => {
    // Check if the click was on the action menu (3-dot menu) or its children
    const target = event.event?.target as HTMLElement;
    if (target) {
      // Look for action menu elements - check for various possible selectors
      const isActionMenu = target.closest('[col-id="actions"]') ||
                          target.closest('.context-menu') ||
                          target.closest('.menu-trigger') ||
                          target.closest('.action-menu') ||
                          (target.classList.contains('ag-cell') && target.getAttribute('col-id') === 'actions');
      
      // Check if the click was on the check-in column
      const isCheckInColumn = target.closest('[col-id="checkedIn"]') ||
                             target.closest('#checkin-checkbox') ||
                             target.id?.includes('checkin-checkbox');
      
      // Check if the click was on the complete column
      const isCompleteColumn = target.closest('[col-id="complete"]') ||
                              target.closest('#complete-checkbox') ||
                              target.id?.includes('complete-checkbox');
      
      if (isActionMenu || isCheckInColumn || isCompleteColumn) {
        return; // Don't handle row click if clicking on action menu, check-in column, or complete column
      }
    }

    const rowData = event.data;
    
    // Check if this row has Enter Results functionality available
    if (rowData && rowData.actions && rowData.actions.canEnterResults) {
      setSelectedResult(rowData.actions.result);
      setShowEnterResults(true);
    }
  }, []);

  const [prevShowId, setPrevShowId] = useState<string | null>(null);

  useEffect(() => {
    // Only reset if the show ID actually changed
    const showIdChanged = prevShowId !== show.id;
    if (showIdChanged) {
      setPrevShowId(show.id);
      setShowSection({ id: "None", value: "None" });
      setShowElement({ id: "None", value: "None" });
      setShowLevel({ id: "None", value: "None" });
      
      // Handle "None" show selection
      if (show.id === "None") {
        setElementOptions([]);
        setLevelOptions([]);
        return;
      }
    }

    // Don't rebuild options if show ID didn't change (prevents clearing user selections)
    if (!showIdChanged) {
      return;
    }

    // Reset section if this show type/club doesn't support the Section filter (only on show change)
    if (!showUsesSectionFilter(show.data)) {
      setShowSection({ id: "None", value: "None" });
    }

    if (show.data !== undefined) {
      // Get the show type elements using the new centralized definitions
      const elementsObj = getShowTypeElements(props.event.sanctioning_club, show.data.show_type);
      
      if (elementsObj) {
        let _elementOptions:IOption[] = [{ id: "None", value: "None" }];
        let hasElements = false;

        // Iterate through the object to determine the structure
        for (const key in elementsObj) {
          const value = elementsObj[key];
          
          // Check if this is an element with levels (object) or a direct level (empty array)
          if (typeof value === 'object' && !Array.isArray(value) && value.levels) {
            // This is an element with levels
            hasElements = true;
            _elementOptions.push({ value: key, id: key, data: show.data });
          } else if (Array.isArray(value) && value.length > 0) {
            // This is an element with an array of levels
            hasElements = true;
            _elementOptions.push({ value: key, id: key, data: show.data });
          }
        }

        setElementOptions(_elementOptions);
        // Direct levels (flat structure like UKC Rally)
        setLevelOptions(getFlatLevelOptions(show.data, elementsObj));

        // Auto-select the element when there is exactly one option (besides "None")
        const realElements = _elementOptions.filter(o => o.id !== "None");
        if (realElements.length === 1) {
          setShowElement(realElements[0]);
        }
      }
    }
  }, [show.id, props.event.sanctioning_club, show.data, prevShowId]);

  useEffect(() => {
    if (showElement.id !== "None" && show.data !== undefined) {
      setLevelOptions([]);
      setShowLevel({ id: "None", value: "None" });

      let _levelOptions:IOption[] = [{ id: "None", value: "None" }];

      // Get the show type elements
      const elementsObj = getShowTypeElements(props.event.sanctioning_club, show.data.show_type);
      
      if (elementsObj && elementsObj[showElement.id]) {
        const elementData = elementsObj[showElement.id];
        
        // Check if it's the new structure (object with levels property) or old structure (array)
        if (typeof elementData === 'object' && !Array.isArray(elementData) && elementData.levels) {
          // New structure: object with levels array
          for (const level of elementData.levels) {
            _levelOptions.push({ value: level, id: level });
          }
        } else if (Array.isArray(elementData)) {
          // Old structure: array of levels
          for (const level of elementData) {
            _levelOptions.push({ value: level, id: level });
          }
        }
      }

      setLevelOptions(_levelOptions);
    } else if (showElement.id === "None" && show.data !== undefined) {
      // When no element is selected, check if this is a flat structure
      const elementsObj = getShowTypeElements(props.event.sanctioning_club, show.data.show_type);
      
      if (elementsObj) {
        // Add back the direct levels (flat structure)
        setLevelOptions(getFlatLevelOptions(show.data, elementsObj));
      }
    }
  }, [showElement, props.event.sanctioning_club, show.data]);

  useEffect(() => {
    if (props.currentTab === props.tabIndex) {
      getResults(props.event._id);
    }
  }, [props.currentTab, props.tabIndex, props.event._id, getResults]);

  useEffect(() => {
    if (!loaded) {
      getResults(props.event._id);
      getShows(props.event.club._id, props.event._id);

      setLoaded(true);
    }
  }, [loaded, getResults, props.event._id, props.event.club._id]);

  // Load run-offs when show changes
  useEffect(() => {
    const isObedienceSanctioned = props.event.sanctioning_club === Constants.sanctioning_club.AKC || props.event.sanctioning_club === Constants.sanctioning_club.UKC;
    const isObedience = show.data?.show_type === Constants.show_type.obedience;

    if (show.id && show.id !== "None" && isObedienceSanctioned && isObedience) {
      loadRunOffs(props.event._id, show.id);
    } else {
      setRunOffs([]);
      setNotifications([]);
    }
  }, [show.id, show.data?.show_type, props.event.sanctioning_club, props.event._id, loadRunOffs]);

  // Handler for submitting run-off placements
  const handleRunOffWinnerSubmit = async (orderedPlacements: Array<{ arm_number: string; dog_name: string; score_id: string; placement: number }>) => {
    if (!selectedRunOff) return;

    const showId =
      selectedRunOff.show_id ||
      (show.id !== "None" ? show.id : "") ||
      (show.data as IShow | undefined)?._id;

    if (!showId || showId === "None") {
      toast.show({
        title: "Error",
        content: "Select the trial this run-off belongs to before saving placements.",
        type: "error"
      });
      return;
    }

    try {
      await apiService.updateRunOffWinner(
        props.event._id,
        showId,
        selectedRunOff.index,
        {
          winner_arm_number: orderedPlacements.find(p => p.placement === 1)?.arm_number || "",
          ordered_placements: orderedPlacements,
          show_element: selectedRunOff.show_element,
          level: selectedRunOff.level,
          placement: selectedRunOff.placement
        }
      );

      toast.show({
        title: "Success",
        content: "Run-off placements have been recorded",
        type: "success"
      });

      // Close the modal
      setShowRunOffModal(false);
      setSelectedRunOff(null);

      // Reload run-offs to update the notification bar
      await loadRunOffs(props.event._id, showId);
    } catch (error: any) {
      toast.show({
        title: "Error",
        content: error.message || "Failed to record run-off placements",
        type: "error"
      });
      throw error;
    }
  };

  useEffect(() => {
    let filter: any = {};

    if (gridApi && show.id !== "None") {
      filter["show_id"] = {
        "values": [ show.id ],
        "filterType": "set",
      };
    }

    if (gridApi && showElement.id !== "None") {
      filter["element"] = {
        "values": [ showElement.value ],
        "filterType": "set"
      };
    }

    if (gridApi && showLevel.id !== "None") {
      filter["level"] = {
        "values": [ showLevel.value ],
        "filterType": "set"
      };
    }

    if (gridApi && showSection.id !== "None" && showUsesSectionFilter(show.data)) {
      filter["section"] = {
        "values": [ showSection.value ],
        "filterType": "set"
      };
    }

    if (gridApi && agilityJumpHeight.id !== "None" && show.data?.show_type === Constants.show_type.agility && props.event.sanctioning_club === Constants.sanctioning_club.AKC) {
      filter["jump_height"] = {
        "values": [ agilityJumpHeight.value ],
        "filterType": "set"
      };
    }

    if (gridApi && filter["show_id"] === undefined && show.id !== "None" && showElement.id !== "None" && showLevel.id !== "None") {
      resetFilters();
    } else if (gridApi) {
      gridApi.setFilterModel(filter);
    }
  }, [gridApi, show, showElement, showLevel, showSection, agilityJumpHeight, resetFilters, props.event.sanctioning_club]);

  // Load breeds data for the judge books filter modal
  useEffect(() => {
    const loadBreeds = async () => {
      try {
        const breedsData = await apiService.getBreeds();
        setBreeds(breedsData);
      } catch (error) {
        // Failed to load breeds
      }
    };

    loadBreeds();
  }, []);

  return (
    <div className={styles.ResultsTab}>
      <div className={styles.actions}>
        <div className={styles.left}>
          <Dropdown className={styles.dropdown} value={show} onChange={(e: any, value :IOption) => { setShow(value); }} label="Show" options={showOptions} placeholder="Select a Show" />
          {!isUKCConformationShow && show.data?.show_type !== Constants.show_type.fast_cat && (
            <Dropdown className={styles.dropdown} disabled={isEmptyOptions(elementOptions)} value={showElement} onChange={(e: any, value :IOption) => { setShowElement(value); }} label="Element" options={elementOptions} placeholder="Select an Element" />
          )}
          {!isUKCConformationShow && show.data?.show_type !== Constants.show_type.fast_cat && (
            <Dropdown className={styles.dropdown} disabled={isEmptyOptions(levelOptions)} value={showLevel} onChange={(e: any, value :IOption) => { setShowLevel(value); }} label="Level" options={levelOptions} placeholder="Select a Level" />
          )}
          {showUsesSectionFilter(show.data) && (
            <Dropdown className={`${styles.dropdown} ${styles.section}`} value={showSection} onChange={(e: any, value :IOption) => { setShowSection(value); }} label="Section" options={getSectionOptionsForShow(show.data)} placeholder="Select a Section" />
          )}
          {(show.data?.show_type === Constants.show_type.agility && props.event.sanctioning_club === Constants.sanctioning_club.AKC) && (
            <Dropdown className={`${styles.dropdown} ${styles.jumpHeight}`} value={agilityJumpHeight} onChange={(e: any, value :IOption) => { setAgilityJumpHeight(value); }} label="Jump Height" options={agilityJumpHeightOptions} placeholder="Select a Jump Height" />
          )}
          <Button className={styles.reset} onClick={() => { resetFilters(); }}>Reset</Button>
          {shouldShowAwardsButton() && (
            <Button className={styles.showAwards} onClick={handleShowAwards}>Awards</Button>
          )}
        </div>
        <div className={styles.right}>
          <ContextMenu
            label={"Actions"}
            options={menuActions}
          />
        </div>
      </div>
      {show.id !== "None" && !!show.data && (
        <div className={styles.showStatusRow}>
          <div className={styles.statusBlock}>
            <span className={styles.statusText}>
              Trial Status: <strong>{getTrialStatusLabel((show.data as IShow).show_status)}</strong>
              {(show.data as IShow).show_status_note ? ` - ${(show.data as IShow).show_status_note}` : ""}
            </span>
            <button
              type="button"
              className={styles.changeStatusLink}
              onClick={() => setShowChangeStatusModal(true)}
            >
              Change Status
            </button>
          </div>
          {selectedShowSupportsRunOrder && (
            <Checkbox
              id="results-show-run-order-checkbox"
              label="Show run order to dog owners"
              value={!!(show.data as IShow)?.show_run_order}
              onChange={handleShowRunOrderToggle}
              disabled={updatingShowRunOrder}
            />
          )}
        </div>
      )}
      {/* Notification Bar for Run-offs */}
      {notifications.length > 0 && (
        <NotificationBar notifications={notifications} />
      )}
      {isUKCConformationShow ? (
        <UKCConformationResults
          event={props.event}
          show={show.data as IShow}
        />
      ) : (
        <div className={styles.tableWrap}>
          <AgGridReact
            onGridReady={onGridReady}
            rowData={rowData}
            suppressRowTransform={true}
            columnDefs={colDefs}
            onFirstDataRendered={onFirstDataRendered}
            onRowClicked={handleRowClick}
            getRowId={(params) => params.data.scoreId}
            defaultColDef={defaultColDef}
            theme={theme}
            pagination={true}
            paginationPageSize={20}
          />
        </div>
      )}
      <Modal
        className={
          modalContent === "email" ? "sendEmailModal sendMessageModal" : 
          modalContent === "judgeBooksModal" ? "judgeBooksModal" : 
          modalContent === "noseworkJudgeBooksModal" ? "noseworkJudgeBooksModal" :
          modalContent === "precisionCoursingJudgeBooksModal" ? "precisionCoursingJudgeBooksModal" :
          modalContent === "showAwards" ? "showAwardsModal" :
          modalContent === "ribbons" || modalContent === "ribbons-small" || modalContent === "ribbons-5162" || modalContent === "scoresheet-labels" ? "ribbonSkipModal downloadModal" :
          "downloadModal"
        }
        isShown={isShown}
        hide={() => { toggle(); }}
        modalContent={getModalContent()}
      />
      
      {/* Enter Results Modal */}
      {showEnterResults && selectedResult && (
        <Modal
          className="enterResultsModal"
          isShown={showEnterResults}
          hide={() => {
            setShowEnterResults(false);
            setSelectedResult(null);
          }}
          modalContent={
            <EnterResults
              event={props.event}
              eventId={props.event._id}
              showType={selectedResult.show_type}
              result={selectedResult}
              show={shows.find(s => s._id === selectedResult.show_id)}
              showScores={scores.filter((s: any) => s.show_id === selectedResult.show_id)}
              onSave={async () => {
                // Close modal
                setShowEnterResults(false);
                setSelectedResult(null);
                
                // For scentwork, refetch the show data to get updated judge schedule
                if (selectedResult.show_type === Constants.show_type.scentwork) {
                  await apiService.getClubEventShows(props.event.club._id, props.event._id).then((response) => {
                    setShows(response);
                  });
                }
                
                // Refresh results data - AG-Grid will automatically maintain scroll position
                await getResults(props.event._id);
                
                // Reload run-offs for Obedience to detect new ties
                if ((props.event.sanctioning_club === Constants.sanctioning_club.AKC || props.event.sanctioning_club === Constants.sanctioning_club.UKC) && 
                    selectedResult.show_type === Constants.show_type.obedience &&
                    show.id && show.id !== "None") {
                  await loadRunOffs(props.event._id, show.id);
                }
              }}
              onClose={() => {
                setShowEnterResults(false);
                setSelectedResult(null);
              }}
            />
          }
        />
      )}

      {/* Run-Off Winner Modal */}
      {showRunOffModal && selectedRunOff && (
        <Modal
          className="runOffModal"
          isShown={showRunOffModal}
          hide={() => {
            setShowRunOffModal(false);
            setSelectedRunOff(null);
          }}
          modalContent={
            <EnterRunOffWinnerModal
              runOffIndex={selectedRunOff.index}
              showElement={selectedRunOff.show_element}
              level={selectedRunOff.level}
              placement={selectedRunOff.placement}
              tiedScores={selectedRunOff.tied_scores}
              tiedScore={selectedRunOff.tied_score}
              onSubmit={handleRunOffWinnerSubmit}
              onCancel={() => {
                setShowRunOffModal(false);
                setSelectedRunOff(null);
              }}
            />
          }
        />
      )}

      {/* Change Status Modal (Trial Status feature) */}
      {showChangeStatusModal && (
        <Modal
          className="changeStatusModal"
          isShown={showChangeStatusModal}
          hide={() => { setShowChangeStatusModal(false); }}
          modalContent={
            <div className={styles.changeStatusModalContent}>
              <h2>Change Status</h2>
              <Dropdown
                label="Trial Status"
                value={statusFormValue}
                onChange={(e: any, value: IOption) => setStatusFormValue(value)}
                options={trialStatusOptions}
                placeholder="Select a status"
              />
              <Input
                type="text"
                label="Note (optional)"
                placeholder="e.g. '10 min delay'"
                maxLength={50}
                value={statusNoteValue}
                onChange={(e: any) => setStatusNoteValue(e.target.value)}
              />
              <div className={styles.charCount}>{statusNoteValue.length}/50</div>
              <Button
                className={styles.saveStatusButton}
                onClick={handleSaveStatus}
                disabled={savingStatus || !statusFormValue.id}
              >
                Save
              </Button>
            </div>
          }
        />
      )}
    </div>
  );
};

export default ResultsTab;